import { CloseButton } from "@components/buttons/close";
import {
  SHAutocomplete,
  SHButton,
  SHDialog,
  SHRichTextEditor,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFeatureTemplate } from "@hooks/useFeatureTemplate";
import {
  CreateFeatureTemplateDTO,
  FeatureTemplateDTO,
  FeatureTemplateSelectionDTO,
} from "@models/feature-template/entities/featureTemplate";
import { DialogContent, DialogTitle, useTheme } from "@mui/material";
import { featureTemplateValidators } from "@pages/platform-analysis/_feature-templates/_id/util";
import { RootState } from "@redux/store";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { isEmpty, take } from "lodash";
import { InformationButton } from "@components/buttons/information";
import { OverwriteTemplateDialog } from "@pages/platform-analysis/components/dialogs/overwrite-template";

interface SaveAsTemplateDialogProps {
  onClose: () => void;
}

export const SaveAsTemplateDialog = ({
  onClose,
}: SaveAsTemplateDialogProps) => {
  const { palette } = useTheme();
  const { featureSelections, review } = useSelector(
    (state: RootState) => state.review,
  );
  const { existingFeatureTemplateForReview, featureTemplateUI } = useSelector(
    (state: RootState) => state.featureTemplate,
  );
  const { practice } = useSelector((state: RootState) => state.practice);
  const { user } = useSelector((state: RootState) => state.auth);
  const { createFeatureTemplate, updateFeatureTemplate } = useFeatureTemplate();
  const [optionFeatureTemplates, setOptionFeatureTemplates] = useState<
    FeatureTemplateSelectionDTO[]
  >([]);

  const {
    control,
    getValues,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm<FeatureTemplateDTO>({
    mode: "onChange",
    defaultValues: { ...new FeatureTemplateDTO() },
    resolver: yupResolver(featureTemplateValidators),
  });

  const [overwriteId, setOverwriteId] = useState<string | undefined>(undefined);

  const isSubmitting = featureTemplateUI?.isSubmitting;

  const handleVerifySubmit = () => {
    const { name } = getValues();
    if (!name) return;

    let overwriteId = existingFeatureTemplateForReview?.find(
      (item) => item?.name === name,
    )?.id;
    if (overwriteId) {
      setOverwriteId(overwriteId);
      return;
    }
    handleOnSubmit();
  };

  const handleOnSubmit = async () => {
    const { name, notes } = getValues();
    if (!overwriteId) {
      const req = {
        name: name,
        notes: notes,
        ownerId: user?.auth0Id,
        ownerName: user?.name,
        ownerAvatarUrl: user?.avatarUrl,
        ownerType: user?.userType,
        adviserFirmId: practice?.id,
        adviserFirmName: practice?.name,
        configurationVersion: review?.configurationVersion,
        sections: featureSelections?.sections,
      } as CreateFeatureTemplateDTO;
      await createFeatureTemplate(req, false);
    } else {
      await updateFeatureTemplate({
        id: overwriteId,
        name: name,
        notes: notes,
        ownerId: user?.auth0Id,
        ownerName: user?.name,
        ownerAvatarUrl: user?.avatarUrl,
        ownerType: user?.userType,
        adviserFirmId: practice?.id,
        adviserFirmName: practice?.name,
        sections: featureSelections?.sections,
      });
    }
    setOverwriteId(undefined);
    onClose();
  };

  useEffect(() => {
    setOptionFeatureTemplates(take(existingFeatureTemplateForReview, 20) ?? []);
  }, [existingFeatureTemplateForReview]);

  return (
    <form>
      <SHDialog
        open
        maxWidth={false}
        sx={{
          "& .MuiPaper-root": {
            width: { xs: "100%", md: 656 },
          },
        }}
      >
        <DialogTitle>
          <SHTypography variant="h2" fontWeight={700} lineHeight={"100%"}>
            Save the current selection as a template?
          </SHTypography>
        </DialogTitle>
        <SHStack
          sx={{
            position: "absolute",
            top: "22px",
            right: "25px",
          }}
        >
          <CloseButton width="12px" height="12px" onClick={onClose} />
        </SHStack>
        <DialogContent
          sx={{
            padding: 0,
            overflowY: "var(--overflow-overlay)",
          }}
        >
          <SHStack spacing={"25px"}>
            <SHTypography
              variant="body2"
              sx={{
                fontSize: "16px",
                margin: "0 40px",
                color: `${palette.text.disabled}`,
              }}
            >
              Fill these fields to help you find the template in the future. You
              can overwrite an existing template by giving it the same name.
            </SHTypography>
            <SHStack
              spacing={3}
              direction="column"
              sx={{ width: { xs: "100%", md: 520 } }}
            >
              <SHAutocomplete
                freeSolo
                disableCloseOnSelect
                disabled={isSubmitting}
                options={optionFeatureTemplates ?? []}
                getOptionLabel={(option) => {
                  if (typeof(option) === "string")
                    return getValues().name;
                  return (option as any)["name"];
                }}
                onInputChange={(e, newValue) => {
                  setValue("name", newValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  if (isEmpty(newValue)) {
                    setOptionFeatureTemplates(
                      take(existingFeatureTemplateForReview, 20) ?? [],
                    );
                  } else {
                    setOptionFeatureTemplates(existingFeatureTemplateForReview ?? []);
                  }
                }}
                renderOption={(props, option) => {
                  return (
                    <li
                      {...props}
                      key={option.id}
                      style={{ padding: "10px 10px" }}
                    >
                      <SHStack
                        alignItems={"center"}
                        direction="row"
                        spacing={1}
                      >
                        <SHTypography variant="body3" fontWeight={500}>
                          {`${option.name} - ${option.selectedFeatures}/${option.totalFeatures} features selected`}
                        </SHTypography>
                        {option?.notes && (
                          <InformationButton content={option?.notes} />
                        )}
                      </SHStack>
                    </li>
                  );
                }}
                textFieldProps={{
                  label: "Template name",
                  placeholder: "Enter name of template",
                  error: !!errors.name,
                  helperText: errors?.name?.message,
                  required: true,
                  hasCounter: true,
                  maxLength: 50,
                }}
                sx={{ width: { xs: "100%", md: 520 } }}
              />
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <SHRichTextEditor
                    {...field}
                    sx={{ width: "100% important" }}
                    label="Notes"
                    placeHolder="Enter notes"
                    maxLength={500}
                    height={165}
                  />
                )}
              />
            </SHStack>
          </SHStack>
          <SHStack
            justifyContent="center"
            alignItems="center"
            direction={"row"}
            spacing={7}
            marginTop={"25px"}
            marginBottom={"15px"}
          >
            <SHButton
              color="primary"
              size="large"
              variant="contained"
              onClick={handleVerifySubmit}
              disabled={isSubmitting || !isValid || !isDirty}
              isLoading={isSubmitting}
              sx={{ width: "410px" }}
            >
              Save as template
            </SHButton>
          </SHStack>
        </DialogContent>
      </SHDialog>
      {overwriteId && (
        <OverwriteTemplateDialog
          onClose={() => setOverwriteId(undefined)}
          onSubmit={handleOnSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </form>
  );
};
